import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SourceBadge } from "@/components/SourceBadge";
import { ExportJsonButton } from "@/components/ExportJsonButton";
import { PlayerFormDialog } from "@/pages/admin/PlayerFormDialog";
import { SPORTS } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

export const PlayersTab = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const load = useCallback(() => api.get("/players", { params: q ? { q } : {} }).then((r) => setItems(r.data.items)), [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const remove = async () => {
    try {
      await api.delete(`/admin/players/${deleting.id}`);
      toast.success(`${deleting.name} deleted`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setDeleting(null);
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <input data-testid="admin-player-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players"
          className="h-11 min-w-0 flex-1 rounded-lg border border-slate-800 bg-[#131927] px-4 text-sm outline-none focus:border-[#FF2D78]" />
        <button data-testid="admin-add-player-button" onClick={() => setEditing({})}
          className="inline-flex h-11 items-center gap-2 rounded-full bg-[#FF2D78] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#E01F66]">
          <Plus className="h-4 w-4" /> Add player
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-[#131927]">
            <tr className="text-left">{["Player", "Sport", "Team", "Stats source", "Testing source", ""].map((h) => <th key={h} className="eyebrow px-5 py-3 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {items.map((p) => (
              <tr key={p.id} data-testid={`admin-player-row-${p.id}`} className="transition-colors hover:bg-[#131927]/60">
                <td className="px-5 py-4">
                  <Link to={`/players/${p.id}`} className="font-semibold hover:text-[#FF2D78]">{p.name}</Link>
                  <div className="text-xs text-slate-500">{p.position}{p.manual_override ? " · Protected from sync" : ""}</div>
                </td>
                <td className="px-5 py-4 text-slate-300">{SPORTS[p.sport]?.label}</td>
                <td className="px-5 py-4 text-slate-300">{p.team}</td>
                <td className="px-5 py-4"><SourceBadge source={p.stats_source} /></td>
                <td className="px-5 py-4"><SourceBadge source={p.testing_source} /></td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <ExportJsonButton player={p} compact testId={`admin-export-player-${p.id}`} />
                    <button data-testid={`admin-edit-player-${p.id}`} onClick={() => setEditing(p)} className="rounded-md p-2 text-slate-400 hover:bg-slate-800 hover:text-white"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`admin-delete-player-${p.id}`} onClick={() => setDeleting(p)} className="rounded-md p-2 text-slate-400 hover:bg-red-500/10 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!items.length && <p className="p-10 text-center text-sm text-slate-500">No players found.</p>}
      </div>
      {editing && <PlayerFormDialog player={editing.id ? editing : null} open onOpenChange={(o) => !o && setEditing(null)} onSaved={load} />}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="border-slate-800 bg-[#131927]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the player and all of her stats and testing results.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" onClick={remove} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
